import { Component, Input, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {combineLatest, firstValueFrom, takeUntil} from 'rxjs';
import { GenericCrudEditComponent } from 'src/app/libs/components/generic-crud/generic-crud-edit.component';
import {
  Checkpoint,
  Container,
  DraftableStateTypes,
  EDraftableStateType,
  EOperationStateType,
  Holiday,
  OperationStateTypeColors,
  OperationResourceAllocations,
  ResourceAllocationEquipment,
  ResourceAllocationTooling,
  ResourceAllocationWorkCenter,
  ResourceAllocationWorker,
  ResourceAllocationWorkforce,
  Routing,
  RoutingOperation,
  TId,
  WorkProcedure,
  WorkProcedureStep,
} from 'src/app/models';
import { GenericResource } from 'src/app/resources';
import { ResourceAllocationAddComponent } from '../resource-allocation-add/resource-allocation-add.component';
import { DxDataGridComponent } from 'devextreme-angular';
import { ToolbarItem } from 'devextreme/ui/data_grid';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';
import { ProcessSheetOperationService } from '../../process-sheet-operation.service';
import {
  CalendarAppointmentAddComponent
} from "../../../../../../../../hr/hr-operative/hr-calendars/hr-calendars-edit/calendar-appointment-add/calendar-appointment-add.component";
import {TabRole} from "../../../../../../../../../libs/components/tabs-host/tabs.service";
import {
  WorkProceduresAddComponent
} from "../../../../../../../../aps/aps-configuration/work-procedures/work-procedures-add/work-procedures-add.component";
import {
  WmsContainerEditComponent
} from "../../../../../../../../wms/wms-operative/wms-locations/wms-container-edit/wms-container-edit.component";
import {WorkProcedureEditComponent} from "../work-procedure-edit/work-procedure-edit.component";
import {
  StepsAddComponent
} from "../../../../../../../../aps/aps-configuration/work-procedures/steps-add/steps-add.component";
import {
  StepsEditComponent
} from "../../../../../../../../aps/aps-configuration/work-procedures/steps-edit/steps-edit.component";

@Component({
  selector: 'app-routing-operations-edit',
  templateUrl: './routing-operations-edit.component.html',
  styleUrl: './routing-operations-edit.component.sass',
  standalone: false,
})
export class RoutingOperationsEditComponent extends GenericCrudEditComponent<RoutingOperation> {

  @Input() routing?: Routing;
  @Input() routingOperation?: RoutingOperation;

  operationTypes = this.api.resources.operations.operationTypes;
  workProcedures = this.api.resources.product.workProcedures;

  stepsAddComponent = StepsAddComponent;
  stepsEditComponent = StepsEditComponent;
  steps?: GenericResource<WorkProcedureStep>;
  checkpoints: Checkpoint[] = [];

  resourceAllocations?: GenericResource<OperationResourceAllocations>;
  resourceAllocationAdd = ResourceAllocationAddComponent;

  @ViewChild('grid', { static: false }) grid!: DxDataGridComponent;

  workcenters = this.api.resources.workcenters.contextualizeQueryParams({
    stateId: EDraftableStateType.Active.toString(),
  });

  workforces = this.api.resources.workforces;
  equipments = this.api.resources.equipments;
  toolings = this.api.resources.toolings;
  workers = this.api.resources.workers;

  editPopupVisible = false;
  editingTooling: any = null;
  editingData: any = null;
  editingQuantity = 1;

  editWorkforcePopupVisible = false;
  editingWorkforce: any = null;
  editingWorkforceData: any = null;
  editingOccupation = 0;

  routingOperations?: RoutingOperation[];

  workProcedure?: WorkProcedure | null;
  operationSteps: WorkProcedureStep[] = [];

  // Stored (not a getter): the `glamResource` directive reads `editingLocked`
  // only once, when the grid is created. We compute this the moment the work
  // procedure loads and before `steps` is assigned (which is what renders the
  // grid), so the grid is always built with the correct locked/editable state.
  isWorkProcedureDraft = false;

  states: {[key: number]: string} = {};
  readonly stateColors: {[key: number]: string} = {
    [EDraftableStateType.Draft]: OperationStateTypeColors[EOperationStateType.Complete],
    [EDraftableStateType.Active]: OperationStateTypeColors[EOperationStateType.Start],
    [EDraftableStateType.Inactive]: OperationStateTypeColors[EOperationStateType.Complete],
    [EDraftableStateType.Obsolete]: OperationStateTypeColors[EOperationStateType.Complete],
  };

  private toolingIdsCache = new WeakMap<ResourceAllocationTooling[], string[]>();
  private workForcesIdsCache = new WeakMap<ResourceAllocationWorkforce[], string[]>();
  private equipmentIdsCache = new WeakMap<ResourceAllocationEquipment[], string[]>();
  private workCenterIdsCache = new WeakMap<ResourceAllocationWorkCenter[], string[]>();
  private workerIdsCache = new WeakMap<ResourceAllocationWorker[], string[]>();

  protected override async preFetch(): Promise<void> {
    this.resource = this.api.resources.product.routingOperations(this.routing!.id!);
    DraftableStateTypes.forEach(s => this.states[s.id] = this.translationService.instant('enumeration.DraftableStateTypes.' + s.name));
    this.checkpoints = await firstValueFrom(this.api.resources.checkpoints.getAll());

    this.routingOperations = await firstValueFrom(
      this.api.resources.product.routingOperations(this.routing!.id!).getAll()
    );

    this.routingOperations = this.routingOperations?.filter(
      op => op.id !== this.routingOperation?.id
    );

    this.entity = this.routingOperation!;

    this.resourceAllocations =
      this.api.resources.product.routingOperationsResourceAllocations(
        this.routing!.id,
        this.routingOperation!.id!
      );

    await this.enrichCurrentOperationSteps(this.routingOperation?.workProcedureId);
  }

  override initFormGroup(): FormGroup<any> {
    return this.fb.group({
      id: [this.routingOperation?.id],
      name: [this.routingOperation?.name],
      operationTypeId: [this.routingOperation?.operationTypeId],
      workProcedureId: [this.routingOperation?.workProcedureId],
      dependencies: [this.routingOperation?.dependencies || []],
    });
  }

  override displayEntity(): string | [string, string] | {
    title: { text: string; icon?: string; color?: string };
    subtitle?: { text: string; icon?: string; color?: string };
  } {
    return {
      title: { text: this.routing?.code + ' ' + this.routing?.name },
      subtitle: { text: this.routingOperation?.name || '' },
    };
  }

  getCheckpointName(id: TId | undefined): string {
    if (id == null || id === '') {
      return '';
    }
    return this.checkpoints.find(c => c.id === id)?.name ?? '';
  }

  formatDefaultValues(value: unknown): string {
    if (value == null) {
      return '';
    }
    if (Array.isArray(value)) {
      return value.map(String).join(', ');
    }
    return String(value);
  }

  protected override async mapFormEntity(): Promise<Partial<RoutingOperation>> {
    if (this.form?.get('dependencies')?.dirty) {
      const data = {
        action: 'Dependencies',
        dependencies: this.form!.value.dependencies,
      };

      await firstValueFrom(
        this.api.routingOpereationsPatch(
          this.routing!.id!,
          this.routingOperation!.id,
          data
        )
      );
    }

    return Promise.resolve(this.form!.value);
  }

  private async enrichCurrentOperationSteps(workProcedureId?: TId): Promise<void> {
    // The *ngIf on the steps grid (and on the images/files tabs) destroys them
    // here and recreates them once the new values are assigned, so the
    // GlamResourceDirective and the gallery/files components re-initialize.
    this.steps = undefined;
    this.workProcedure = null;
    this.isWorkProcedureDraft = false;

    if (!workProcedureId) {
      this.workProcedure = null;
      this.operationSteps = [];
      return;
    }

    this.routingOperation!.workProcedureId = workProcedureId;

    this.workProcedure = await firstValueFrom(
      this.api.resources.product.workProcedures.get(workProcedureId)
    );
    this.isWorkProcedureDraft = this.workProcedure?.stateId === EDraftableStateType.Draft;

    const rawSteps = await firstValueFrom(
      this.api.resources.product.workProceduresSteps(workProcedureId).getAll()
    );

    this.operationSteps = [...rawSteps].sort(
      (a, b) => Number(a.order ?? 0) - Number(b.order ?? 0)
    );

    this.steps = this.api.resources.product.workProceduresSteps(workProcedureId);
  }

  async onWorkProcedureChange(e: any): Promise<void> {

    this.routingOperation!.workProcedureId = e.selectedItem.id;

    await this.enrichCurrentOperationSteps( e.selectedItem.id);

    await firstValueFrom(this.api.resources.product.routingOperations(this.routing!.id!).put(this.routingOperation!))
  }

  addWorkProcedure(): void {
    const tab = this.tabs?.createTab(WorkProceduresAddComponent, TabRole.CREATION, {inputPrms: {documentTypeId: this.routing?.documentTypeId}});
    tab.component.instance.entityCreated.pipe(
      takeUntil(combineLatest([this.destroy$])),
    ).subscribe( async entity =>{
      const newWorkProcedure = entity as WorkProcedure;
      this.routingOperation!.workProcedureId = newWorkProcedure.id;
      await this.enrichCurrentOperationSteps(newWorkProcedure.id);
      await firstValueFrom(this.api.resources.product.routingOperations(this.routing!.id!).put(this.routingOperation!));
      this.tabs.closeTab(tab)});
  }

  async editWorkProcedure(): Promise<void> {
    const workProcedure = await firstValueFrom(this.api.resources.product.workProcedures.get(this.routingOperation!.workProcedureId))
    const tab = this.tabs.createTab<GenericCrudEditComponent<WorkProcedure>>(WorkProcedureEditComponent, TabRole.EDITION, {inputPrms: {workProcedure: workProcedure }});
    tab.component.instance.entityChanged.pipe(
      takeUntil(combineLatest([this.destroy$])),
    ).subscribe( async entity =>{
      await this.enrichCurrentOperationSteps( entity.id);
      this.tabs.closeTab(tab)});
  }

  async copyWorkProcedure(): Promise<void> {
    try {
      const workProcedure = await firstValueFrom(this.api.workProcedureCopy(this.routingOperation!.workProcedureId!));
      this.routingOperation!.workProcedureId = workProcedure.id;
      await this.enrichCurrentOperationSteps(workProcedure.id);
      await firstValueFrom(this.api.resources.product.routingOperations(this.routing!.id!).put(this.routingOperation!))
    } catch (error) {
      console.log(error);
    }
  }

  getToolingIds(toolings: ResourceAllocationTooling[]): string[] {
    if (!toolings) return [];
    if (!this.toolingIdsCache.has(toolings)) {
      this.toolingIdsCache.set(toolings, toolings.map(t => t.toolingId));
    }
    return this.toolingIdsCache.get(toolings)!;
  }

  getWorkForcesIds(workforces: ResourceAllocationWorkforce[]): string[] {
    if (!workforces) return [];
    if (!this.workForcesIdsCache.has(workforces)) {
      this.workForcesIdsCache.set(workforces, workforces.map(t => t.workforceId));
    }
    return this.workForcesIdsCache.get(workforces)!;
  }

  getEquipmentIds(equipments: ResourceAllocationEquipment[]): string[] {
    if (!equipments) return [];
    if (!this.equipmentIdsCache.has(equipments)) {
      this.equipmentIdsCache.set(equipments, equipments.map(e => e.equipmentId));
    }
    return this.equipmentIdsCache.get(equipments)!;
  }

  getWorkCenterIds(workCenters: ResourceAllocationWorkCenter[]): string[] {
    if (!workCenters) return [];
    if (!this.workCenterIdsCache.has(workCenters)) {
      this.workCenterIdsCache.set(workCenters, workCenters.map(w => w.workCenterId));
    }
    return this.workCenterIdsCache.get(workCenters)!;
  }

  getWorkerIds(workers: ResourceAllocationWorker[]): string[] {
    if (!workers) return [];
    if (!this.workerIdsCache.has(workers)) {
      this.workerIdsCache.set(workers, workers.map(w => w.workerId));
    }
    return this.workerIdsCache.get(workers)!;
  }

  async saveWorkcenters(data: any, e: any): Promise<void> {
    const ids: string[] = e.value;

    if (this.arraysEqual(data.data.workCentersIds, ids)) {
      return;
    }

    data.data.workCentersIds = ids;

    const response = await firstValueFrom(
      this.api.routingOpereationsResourceAllocationsPatch(
        this.routing!.id!,
        this.routingOperation!.id!,
        data.data.id,
        { action: 'WorkCenters', workCenters: ids }
      )
    );

    this.updateRowFromResponse(data, response);
  }

  async saveEquipments(data: any, e: any): Promise<void> {
    const ids: string[] = e.value;

    if (this.arraysEqual(data.data.equipmentIds, ids)) {
      return;
    }

    data.data.equipmentIds = ids;

    const response = await firstValueFrom(
      this.api.routingOpereationsResourceAllocationsPatch(
        this.routing!.id!,
        this.routingOperation!.id!,
        data.data.id,
        { action: 'equipments', equipments: ids }
      )
    );

    this.updateRowFromResponse(data, response);
  }

  async saveWorkforces(data: any, e: any): Promise<void> {
    const ids: string[] = e.value;

    if (this.arraysEqual(data.data.workforceIds, ids)) {
      return;
    }

    data.data.workforceIds = ids;

    const payload = {
      action: 'workforces',
      workforces: ids.map(id => ({
        workforceId: id,
        occupation: 1,
      })),
    };

    const response = await firstValueFrom(
      this.api.routingOpereationsResourceAllocationsPatch(
        this.routing!.id!,
        this.routingOperation!.id!,
        data.data.id,
        payload
      )
    );

    this.updateRowFromResponse(data, response);
  }

  async saveToolings(data: any, e: any): Promise<void> {
    const ids: string[] = e.value;

    if (this.arraysEqual(data.data.toolingIds, ids)) {
      return;
    }

    data.data.toolingIds = ids;

    const payload = {
      action: 'toolings',
      toolings: ids.map(id => ({
        toolingId: id,
        quantity: 1,
      })),
    };

    const response = await firstValueFrom(
      this.api.routingOpereationsResourceAllocationsPatch(
        this.routing!.id!,
        this.routingOperation!.id!,
        data.data.id,
        payload
      )
    );

    this.updateRowFromResponse(data, response);
  }

  async saveWorkers(data: any, e: any): Promise<void> {
    const ids: string[] = e.value;

    if (this.arraysEqual(data.data.workerIds, ids)) {
      return;
    }

    data.data.workerIds = ids;

    const response = await firstValueFrom(
      this.api.routingOpereationsResourceAllocationsPatch(
        this.routing!.id!,
        this.routingOperation!.id!,
        data.data.id,
        { action: 'Workers', workers: ids }
      )
    );

    this.updateRowFromResponse(data, response);
  }

  private arraysEqual(a: string[] = [], b: string[] = []): boolean {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }

  private updateRowFromResponse(data: any, response: any): void {
    Object.assign(data.data, response);

    data.data.workCentersIds = response.workCenters?.map((w: any) => w.workCenterId) ?? [];
    data.data.equipmentIds = response.equipments?.map((e: any) => e.equipmentId) ?? [];
    data.data.workerIds = response.workers?.map((w: any) => w.workerId) ?? [];
    data.data.workforceIds = response.workforces?.map((w: any) => w.workforceId) ?? [];
    data.data.toolingIds = response.toolings?.map((t: any) => t.toolingId) ?? [];

    data.component.repaintRows([data.rowIndex]);
  }

  async onSelectionChanged(): Promise<void> {
    const ids = await this.grid?.instance.getSelectedRowKeys();
    const onlyOneRowSelected = ids?.length === 1;
    this.setButtonCopyState(onlyOneRowSelected);
  }

  private setButtonCopyState(enabled: boolean): void {
    (this.grid?.toolbar?.items as Array<ToolbarItem>).forEach(item => {
      if (item.name === 'copy') {
        item.disabled = !enabled;
      }
    });
  }

  async copyRow(): Promise<void> {
    const data = await this.grid.instance.getSelectedRowsData();

    await firstValueFrom(
      this.api.routingOpereationsResourceAllocationsCopy(
        this.routing!.id!,
        this.routingOperation!.id,
        data[0].id
      )
    );

    this.grid.instance.refresh();
  }

  onReorder = async (e: DxDataGridTypes.RowDraggingReorderEvent): Promise<void> => {
    if (!e.component || e.fromIndex === e.toIndex) {
      return;
    }

    const grid = e.component;
    const dataSource = grid.getDataSource().items();
    const ids = dataSource.map(d => (d as any).id);

    const movedId = ids.splice(e.fromIndex, 1)[0];
    ids.splice(e.toIndex, 0, movedId);

    await firstValueFrom(
      this.api.routingOpereationsResourceAllocationsReorder(
        this.routing!.id!,
        this.routingOperation!.id!,
        movedId,
        e.toIndex
      )
    );

    const newData = this.reorderByRules(dataSource, ids);

    grid.option('dataSource', newData);
  };

  reorderByRules<T>(data: T[], rules: any[]): T[] {
    return rules
      .map(ruleId => data.find(item => (item as any).id === ruleId))
      .filter((item): item is T => item !== undefined);
  }

  concatNames(elements: any[]): string {
    return elements
      .map(e => {
        if ('toolingName' in e) return e.toolingName + ' (' + e.quantity + ')';
        if ('equipmentName' in e) return e.equipmentName;
        if ('workCenterName' in e && 'sectionName' in e) return e.sectionName + ' | ' + e.workCenterName;
        if ('workforceName' in e) return e.workforceName + ' (' + (e.occupation * 100) + ' %)';
        if ('workerName' in e) return e.workerName;
        return '';
      })
      .join(', ');
  }

  getQuantityForTag(
    toolingId: string,
    value: ResourceAllocationTooling[] | null | undefined
  ): number {
    if (!value) return 1;
    const tooling = value.find(t => t.toolingId === toolingId);
    return tooling?.quantity ?? 1;
  }

  onToolingClick(event: MouseEvent, item: any, data: any): void {
    event.stopPropagation();

    const toolingInData = data.value?.find((t: any) => t.toolingId === item.id);
    if (!toolingInData) return;

    this.editingTooling = item;
    this.editingData = data;
    this.editingQuantity = this.getQuantityForTag(item.id, data.value);
    this.editPopupVisible = true;
  }

  async applyQuantityChange(): Promise<void> {
    if (!this.editingTooling || !this.editingData) return;

    const toolings = (this.editingData.value || []).map((t: any) => ({
      toolingId: t.toolingId ?? t.id,
      quantity:
        this.editingTooling.id === t.toolingId || this.editingTooling.id === t.id
          ? this.editingQuantity
          : t.quantity ?? 1,
    }));

    const payload = {
      action: 'toolings',
      toolings,
    };

    const response = await firstValueFrom(
      this.api.routingOpereationsResourceAllocationsPatch(
        this.routing!.id!,
        this.routingOperation!.id!,
        this.editingData.data.id,
        payload
      )
    );

    const rowIndex = this.grid.instance.getRowIndexByKey(this.editingData.data.id);

    if (rowIndex >= 0) {
      const rowData = this.grid.instance.getDataSource().items()[rowIndex];
      Object.assign(rowData, response);
      this.grid.instance.repaintRows([rowIndex]);
    }

    this.editPopupVisible = false;
    this.editingTooling = null;
    this.editingData = null;
  }

  getOccupationForTag(
    id: string,
    value: ResourceAllocationWorkforce[] | null | undefined
  ): number {
    return value?.find(v => v.workforceId === id)?.occupation ?? 0;
  }

  onWorkforceClick(event: MouseEvent, item: any, data: any): void {
    event.stopPropagation();

    const workforceInData = data.value?.find((t: any) => t.workforceId === item.id);
    if (!workforceInData) return;

    this.editingWorkforce = item;
    this.editingWorkforceData = data;
    this.editingOccupation = this.getOccupationForTag(item.id, data.value);
    this.editWorkforcePopupVisible = true;
  }

  async applyOccupationChange(): Promise<void> {
    if (!this.editingWorkforce || !this.editingWorkforceData) return;

    const workforces: ResourceAllocationWorkforce[] =
      (this.editingWorkforceData.value || []).map((w: any) => ({
        workforceId: w.workforceId ?? w.id,
        occupation:
          this.editingWorkforce.id === w.workforceId || this.editingWorkforce.id === w.id
            ? this.editingOccupation
            : w.occupation ?? 0,
      }));

    const payload = {
      action: 'workforces',
      workforces,
    };

    const response = await firstValueFrom(
      this.api.routingOpereationsResourceAllocationsPatch(
        this.routing!.id!,
        this.routingOperation!.id!,
        this.editingWorkforceData.data.id,
        payload
      )
    );

    const rowIndex = this.grid.instance.getRowIndexByKey(this.editingWorkforceData.data.id);

    if (rowIndex >= 0) {
      const rowData = this.grid.instance.getDataSource().items()[rowIndex];
      Object.assign(rowData, response);
      this.grid.instance.repaintRows([rowIndex]);
    }

    this.editWorkforcePopupVisible = false;
    this.editingWorkforce = null;
    this.editingWorkforceData = null;
  }

}
